import {
  Component,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
  EventEmitter,
  Output,
  HostListener,
  Renderer2,
  ElementRef,
  Input,
  Optional,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { PublicSettingsService } from "../../../_services/app-settings/public-settings.service";
import { SESSION_LAUNCH_WINDOW_MINUTES } from "../../../_constants/session-rules";
import { FeedbackModalComponent } from "../../../student-voice/feedback-modal/feedback-modal.component";
import { SendStudentFeedbackLinkModalComponent } from "../../../student-voice/send-student-feedback-link-modal/send-student-feedback-link-modal.component";
import { FeatureHelperService } from "../../../_helpers/feature-helper/feature-helper.service";
import { TutorReportsHelperService } from "../../../_helpers/tutor-reports-helper/tutor-reports-helper.service";
import { PlacementData, PlacementStudentV2 } from "../../../_models";
import { SendStudentFeedbackLinkModalData } from "../../../_models/student-feedback/send-student-feedback-link-modal-data.model";
import {
  AuthenticationService,
  PlacementService,
  RoleService,
  SessionService,
} from "../../../_services";
import { ThemeService } from "../../../_helpers/theming-engine/theme.service";
import { PlacementMaintenance } from "../../../_models/maintenance/placement-maintenance.model";
import { StudentBookingStudentDetails } from "../../../student-bookings";
import { EditLearningSectionModalComponent } from "../../tutor-report-timeline/modals/edit-learning-section-modal/edit-learning-section-modal.component";
import { ErrorModalHelperService } from "../../../_helpers/error-modal-helper/error-modal-helper.service";
import { TimelineSectionStudentInfo } from "../../../_models/timeline-models/timeline-section-student-info.model";
import { PlacementSession } from "../../../_models/tutor-reports/placement-session.model";
import { LaunchDigitalClassroom } from "../../../_models/digital-classroom/digital-classroom-launch-session.model";
import { DigitalClassroomAccess } from "../../../_models/digital-classroom/digital-classroom-access.model";
import { LaunchVideoLibrary } from "../../../_models/digital-classroom/digital-classroom-launch-library.model";
import { DynamicConfirmModalComponent } from "../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component";
import { RiskAssessmentDialogComponent } from "../tutor-report-learner-journey-flow/dialogs/risk-assessment-dialog/risk-assessment-dialog.component";
import { RiskAssessmentService } from "../../../_services/risk-assessment/risk-assessment.service";
import { RiskAssessment } from "../../../_models/risk-assessment/risk-assessment.model";
import { FeatureSwitchService } from "../../../_services/feature-switch/feature-switch.service";
import { FeatureSwitchKeys } from "../../../_constants/feature-switch-keys";

@Component({
    selector: "tutor-report-learner-journey-flow",
    templateUrl: "./tutor-report-learner-journey-flow.component.html",
    styleUrls: ["./tutor-report-learner-journey-flow.component.css"],
    standalone: false
})
export class TutorReportLearnerJourneyFlowComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<void> = new Subject<void>();
  @Output() onDataChanged = new EventEmitter<boolean>();
  @Input() lessonPlanType: string = 'LearnerJourney';
  @ViewChild("utilityBarForm", { static: true })
  utilityBarForm: TemplateRef<any>;
  @ViewChild("container", { static: true }) container: ElementRef;

  public placementData: PlacementData;
  public startDate: Date;
  public endDate: Date;
  public students: Array<PlacementStudentV2> = new Array<PlacementStudentV2>();
  public studentFeedbackPermission: boolean = false;
  public voiceExpanded: boolean = false;
  public showLaunchOnlineRoomButton: boolean = false;
  public showLaunchDigitalClassroom: boolean = false;
  // Online booking whose Digital Classroom has not been provisioned yet (Task 2 UX decision:
  // a visible amber banner, never silence). The sweep normally fills the room within minutes.
  public showMissingRoomWarning: boolean = false;
  // sessions.delivery-setting (39883): an in-person booking can be set up to run sessions in the Digital Classroom.
  public showOfferOnlineClassroom: boolean = false;
  public offeringOnlineClassroom: boolean = false;
  private deliverySettingOn: boolean = false;

  // ---- Launch window ------------------------------------------------------------------------
  // A session can only be launched from launchWindowMinutes (90 unless an admin changes the served
  // sessions.launch-window-minutes setting) before it starts to the same after it ends.
  // That rule used to be enforced ONLY inside the click handler, so the button looked usable at
  // all times and the tutor discovered the rule by being refused. These drive a disabled tile and
  // a plain-English hint instead, matching how the student home page has always behaved.
  public launchWindowOpen: boolean = false;
  /** e.g. "Opens 11:30" / "Closes 15:30" / "Ended" — the reason the tile is disabled. */
  public launchWindowHint: string = "";
  /** The session the window refers to; a booking has many, so say which one. */
  public launchWindowSessionLabel: string = "";

  /** The served launch window (api/Settings/Public); SESSION_LAUNCH_WINDOW_MINUTES until it arrives or if it fails. */
  public launchWindowMinutes: number = SESSION_LAUNCH_WINDOW_MINUTES;
  /** serverTime - clientTime, so a skewed browser clock cannot open the tile early. */
  private serverClockOffsetMs: number = 0;
  private launchWindowTimer: any = null;
  public hasOnlineOverviewClientPermission: boolean = false;
  public hasOnlineOverviewAdminPermission: boolean = false;
  /** VideoLibraryAllRooms — the one feature behind every Digital Classroom SuperUser launch. */
  public hasVideoLibraryAllRoomsPermission: boolean = false;

  public placementMaintenance: PlacementMaintenance =
    new PlacementMaintenance();
  public clientId: number = 0;
  public session: PlacementSession = new PlacementSession();
  public hasLaunchDigitalClassroomPermission: boolean = false;
  public hasLaunchVideoLibraryPermission: boolean = false;
  public hasOnlineSessionSetupPermission: boolean = false;
  private userId: any;
  private isSuperUser: boolean = false;
  public scrollTop: number;
  public parallax: boolean = true;
  public hasSession: boolean = false;
  public sessions: Array<PlacementSession> = new Array<PlacementSession>();
  public riskAssessmentSets: Array<RiskAssessment> = new Array<RiskAssessment>();

  constructor(
    public themeService: ThemeService,
    public tutorReportsHelperService: TutorReportsHelperService,
    private router: Router,
    private authService: AuthenticationService,
    private featureHelperService: FeatureHelperService,
    private dialog: MatDialog,
    private readonly placementService: PlacementService,
    private renderer: Renderer2,
    private errorModalHelper: ErrorModalHelperService,
    private sessionService: SessionService,
    private readonly riskAssessmentService: RiskAssessmentService,
    @Optional() private publicSettings: PublicSettingsService = null,
    @Optional() private featureSwitches: FeatureSwitchService = null,
  ) {}

  @HostListener("window:scroll")
  scrollFn() {
    this.scrollTop = window.pageYOffset;
    if (this.parallax == true) {
      this.renderer.setStyle(
        this.container.nativeElement,
        "background-position-y",
        Math.floor(window.pageYOffset / 1.8) + "px"
      );
    }
  }

  ngOnInit() {
    this.watchLaunchWindowSetting();
    this.featureSwitches?.isEnabled(FeatureSwitchKeys.sessionDeliverySetting)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((on) => {
        this.deliverySettingOn = on;
        this.updateOfferOnlineClassroom();
      });

    let featureList = new Array<string>();
    featureList.push(
      "TutorReportsView",
      "StudentFeedbackAdmin",
      "LaunchDigitalClassroom",
      "LaunchVideoLibrary",
      "OnlineSessionSetup",
      "OnlineOverviewClient",
      "OnlineOverviewAdmin"
    );

    let currentUserFeatures =
      this.featureHelperService.checkFeaturePermissions(featureList);

    if (
      !currentUserFeatures.filter((f) => f.feature === "TutorReportsView")[0]
        .enabled
    ) {
      console.error(
        "User does not have th required permissions to view the tutor reports screen"
      );
      this.router.navigate(["/login"], {
        queryParams: { returnUrl: window.location.pathname },
      });
    }

    this.studentFeedbackPermission = currentUserFeatures.find(
      (f) => f.feature == "StudentFeedbackAdmin"
    ).enabled;
    this.hasLaunchDigitalClassroomPermission = currentUserFeatures.find(
      (f) => f.feature == "LaunchDigitalClassroom"
    ).enabled;
    this.hasLaunchVideoLibraryPermission = currentUserFeatures.find(
      (f) => f.feature == "LaunchVideoLibrary"
    ).enabled;
    this.hasOnlineSessionSetupPermission = currentUserFeatures.find(
      (f) => f.feature == "OnlineSessionSetup"
    ).enabled;
    this.hasOnlineOverviewClientPermission = currentUserFeatures.find(
      (f) => f.feature == "OnlineOverviewClient"
    ).enabled;
    this.hasOnlineOverviewAdminPermission = currentUserFeatures.find(
      (f) => f.feature == "OnlineOverviewAdmin"
    ).enabled;
    this.hasVideoLibraryAllRoomsPermission = this.featureHelperService.hasFeature("VideoLibraryAllRooms");

    this.userId = this.authService.userSessionData.currentUser.id;
    this.hasOnlineSessionSetupPermission = true;

    this.tutorReportsHelperService.placementData$
      .pipe(
        filter((d): d is PlacementData => d != null),
        takeUntil(this.unsubscribe)
      )
      .subscribe((pd) => {
        this.placementData = pd;
        this.populatePage();
        this.loadRiskAssessments();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.launchWindowTimer) {
      clearInterval(this.launchWindowTimer);
      this.launchWindowTimer = null;
    }
  }

  /** Server time, not the browser's — the launch check on the API side uses the server clock. */
  private serverNow(): Date {
    return new Date(Date.now() + this.serverClockOffsetMs);
  }

  /**
   * Learns the server/client clock difference once, then re-evaluates the window on a timer so a
   * page left open crosses the boundary on its own rather than needing a refresh.
   */
  private startLaunchWindowTracking(): void {
    this.placementService.getDateTimeNow().subscribe({
      next: (response: Date) => {
        this.serverClockOffsetMs = new Date(response).getTime() - Date.now();
        this.updateLaunchWindow();
      },
      // A failed time read must not hide the button — fall back to the browser clock and let the
      // click handler, which reads server time itself, remain the authority.
      error: () => this.updateLaunchWindow(),
    });

    if (!this.launchWindowTimer) {
      this.launchWindowTimer = setInterval(() => this.updateLaunchWindow(), 30000);
    }
  }

  /** Applies the served launch window (sessions.launch-window-minutes) as soon as it arrives. */
  private watchLaunchWindowSetting(): void {
    this.publicSettings?.sessionLaunchWindowMinutes().pipe(takeUntil(this.unsubscribe)).subscribe(minutes => {
      this.launchWindowMinutes = minutes;
      this.updateLaunchWindow();
    });
  }

  /** Whole calendar days from today to the given date — 0 today, 1 tomorrow, negative in the past. */
  private calendarDaysAway(target: Date, now: Date): number {
    const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((a.getTime() - b.getTime()) / 86400000);
  }

  /**
   * How far off a session is, in the words someone would actually use: "today", "tomorrow", the
   * weekday while it is still nearby, and a real date once it is far enough that a weekday alone
   * would be ambiguous. "Friday" is only useful within the coming week.
   */
  private relativeDayLabel(target: Date, now: Date): string {
    const days = this.calendarDaysAway(target, now);
    if (days === 0) return "today";
    if (days === 1) return "tomorrow";
    if (days > 1 && days < 7) return target.toLocaleDateString([], { weekday: "long" });
    return target.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  }

  /** Recomputes whether the next session is launchable, and what to tell the tutor if it is not. */
  private updateLaunchWindow(): void {
    if (!this.hasSession || !this.session) {
      this.launchWindowOpen = false;
      this.launchWindowHint = "";
      this.launchWindowSessionLabel = "No sessions scheduled on this booking";
      return;
    }

    const now = this.serverNow();
    const start = this.sessionStart(this.session);
    const end = this.sessionEnd(this.session);

    const opens = new Date(start);
    const closes = new Date(end);
    opens.setMinutes(opens.getMinutes() - this.launchWindowMinutes);
    closes.setMinutes(closes.getMinutes() + this.launchWindowMinutes);

    this.launchWindowOpen = now >= opens && now <= closes;

    const time = (d: Date) =>
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });

    // Bug 37074. This label used to be unconditionally "Next session", whatever the session's
    // relationship to now. During the 90-minute grace after a session ends — which the two earlier
    // fixes deliberately made reachable — a tutor who had just finished teaching was shown
    // "Next session: today 13:00-14:00" for the lesson they had only just left. The section was
    // working; it was describing itself wrongly, and reads as the app offering a future session
    // early. That is what Leo reported, and it is why fixing the SELECTION twice did not fix it:
    // those changed WHICH session is offered, this is what it is CALLED.
    //
    // "Next session: today 13:00-14:00" / "Session in progress: today 13:00-14:00" /
    // "Session ended: today 13:00-14:00"
    const prefix =
      now > end ? "Session ended"
        : now >= start ? "Session in progress"
          : "Next session";

    this.launchWindowSessionLabel =
      `${prefix}: ${this.relativeDayLabel(start, now)} ${time(start)}–${time(end)}`;

    if (this.launchWindowOpen) {
      this.launchWindowHint = "";
      return;
    }

    if (now < opens) {
      // Say the day whenever the window does not open today — "Launch opens 08:30" is misleading
      // when the session is on Friday, which is the case the tutor most needs to read correctly.
      const opensToday = this.calendarDaysAway(opens, now) === 0;
      this.launchWindowHint = opensToday
        ? `Launch opens at ${time(opens)}`
        : `Launch opens ${this.relativeDayLabel(opens, now)} at ${time(opens)}`;
    } else {
      this.launchWindowHint = "This session's launch window has closed";
    }
  }

  public refreshTutorReport(): void {
    this.populatePage();
  }

  public get hasSubmittedRiskAssessment(): boolean {
    return this.riskAssessmentSets.some((set) => set.submitted);
  }

  /** Tile-label data: the placement's risk assessment sets. Errors degrade to 'Create' silently —
   *  an error modal on every page load would be noisier than a wrong tile label. */
  private loadRiskAssessments(): void {
    // Warm the session-cached risk lookup so the dialog's first open only waits on the sets call.
    this.riskAssessmentService
      .getLookup()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({ error: () => {} });
    this.riskAssessmentService
      .getByPlacement(this.placementData.placementID)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (sets) => (this.riskAssessmentSets = sets),
        error: () => (this.riskAssessmentSets = []),
      });
  }

  public openRiskAssessmentDialog(): void {
    const dialogRef = this.dialog.open(RiskAssessmentDialogComponent, {
      width: "920px",
      maxWidth: "95vw",
      disableClose: true,
      data: { placementId: this.placementData.placementID },
    });

    dialogRef.afterClosed().subscribe((sets: RiskAssessment[] | null) => {
      if (sets) {
        this.riskAssessmentSets = sets;
      }
    });
  }

  public launchOnlineLesson(): void {
    window.open(
      this.placementData.onlineRoomAccessUrl,
      "_blank"
    );
  }

  public launchPrepOnlineRoom(): void {
    //TODO go to api for time

    this.placementService.getDateTimeNow().subscribe({
      next: (response: Date) => {
        let currentDateTime = new Date(response);
        let startCompareTime = currentDateTime;
        let endCompareTime = currentDateTime;

        if (this.hasSession) {
          const sessionDate = new Date(this.session.startDate);

          startCompareTime = new Date(
            sessionDate.getFullYear(),
            sessionDate.getMonth(),
            sessionDate.getDate(),
            parseInt(this.session.startTime.split(":")[0], 10),
            parseInt(this.session.startTime.split(":")[1], 10)
          );

          endCompareTime = new Date(
            sessionDate.getFullYear(),
            sessionDate.getMonth(),
            sessionDate.getDate(),
            parseInt(this.session.endTime.split(":")[0], 10),
            parseInt(this.session.endTime.split(":")[1], 10)
          );
        }

        // Check if now is within 30 minutes of start time || has started
        const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
        const isWithinThirtyMinutesOfSession =
          Math.abs(currentDateTime.getTime() - startCompareTime.getTime()) <=
          thirtyMinutes;
        const isDuringSession =
          currentDateTime > startCompareTime &&
          currentDateTime < endCompareTime;

        if (isWithinThirtyMinutesOfSession || isDuringSession) {
          let errorMessage = isDuringSession
            ? "Your next session has already started. Launch the online classroom to enter the session."
            : "Your next session starts within 30 minutes. Launch the online classroom to enter the session.";
          this.errorModalHelper.show(errorMessage, {
            message: null,
            status: null,
            isValidationError: true,
          });
        } else {
          const dialogRef = this.dialog.open(DynamicConfirmModalComponent, {
            data: {
              confirmMessage:
                "This  session should not be used to conduct lessons. It is to allow for the room to be prepared prior to a lesson or cleaned up after a lesson so that it does not become part of the digital library. All data is captured and recorded for safeguarding purposes. If you have taught a lesson in this room please contact your branch immediately.",
            },
            panelClass: "my-confirm-dialog",
          });
          dialogRef.afterClosed().subscribe((confirm: boolean) => {
            if (confirm) {
              this.launchDigitalClassroom(true);
            }
          });
        }
      },
      error: (error: any) => {
        let errorMessage =
          error.status === 401 ? "Unauthorized" : error.message;

        if (
          errorMessage ==
          "Object reference not set to an instance of an object."
        ) {
          errorMessage = "Client not found. Try another.";
        }

        this.errorModalHelper.show(errorMessage, error);
      },
      complete: () => {},
    });
  }

  public async launchDigitalClassroom(isNonSession: boolean) {
    //TODO go to api for time
    this.placementService.getDateTimeNow().subscribe({
      next: (response: Date) => {
        // check if the session selected is still the correct session if there is more than one session on that day and create them
        if (this.sessions.length > 1) {
          this.createMultipleSessions();
        }

        let isWithinNinetyMinutes = false;

        let currentDateTime = new Date(response);

        if (this.hasSession) {
          let startTime: Date = new Date(this.session.startDate);
          let endTime: Date = new Date(this.session.startDate);

          startTime.setHours(
            parseInt(this.session.startTime.split(":")[0], 10),
            parseInt(this.session.startTime.split(":")[1], 10),
            0
          );
          endTime.setHours(
            parseInt(this.session.endTime.split(":")[0], 10),
            parseInt(this.session.endTime.split(":")[1], 10),
            0
          );

          startTime.setMinutes(startTime.getMinutes() - this.launchWindowMinutes);
          endTime.setMinutes(endTime.getMinutes() + this.launchWindowMinutes);

          isWithinNinetyMinutes = this.isCurrentTimeBetween(
            startTime,
            endTime,
            currentDateTime
          );
        }

        if (isWithinNinetyMinutes || isNonSession) {
          
          const request: LaunchDigitalClassroom =
            this.createLaunchDigitalClassroomModel(this.session, isNonSession);

          this.placementService.launchDigitalClassroom(request).subscribe(
            (access: DigitalClassroomAccess) => {
              setTimeout(() => {
                window.open(access.accessToken, "_blank");
              }, 0);
            },
            (error) => {
              let errorMessage =
                error.status === 401 ? "Unauthorized" : error.message;
              if (
                errorMessage ==
                "Object reference not set to an instance of an object."
              ) {
                errorMessage = "Client not found. Try another.";
              }
              this.errorModalHelper.show(errorMessage, error);
            }
          );
        } else {
          let errorMessage =
            "You can only launch your session within " + this.launchWindowMinutes + " minutes of its start time or end time. Please use the prep room button to launch and prepare your session before this. If you have re-arranged your session agreed times please contact your branch.";
          this.errorModalHelper.show(errorMessage, {
            message: null,
            status: null,
            isValidationError: true,
          });
        }
      },
      error: (error: any) => {
        let errorMessage =
          error.status === 401 ? "Unauthorized" : error.message;

        if (
          errorMessage ==
          "Object reference not set to an instance of an object."
        ) {
          errorMessage = "Client not found. Try another.";
        }

        this.errorModalHelper.show(errorMessage, error);
      },
      complete: () => {},
    });
  }

  private createLaunchDigitalClassroomModel(
    session: PlacementSession,
    isNonSession: boolean,
    isSetupSession: boolean = false
  ): LaunchDigitalClassroom {
    const request: LaunchDigitalClassroom = {
      placementId: this.placementMaintenance.placementInfo.placementId,
      startDate: session.startDate,
      startTime: session.startTime,
      endTime: session.endTime,
      digitalClassroomId:
        this.placementMaintenance.placementInfo.digitalClassroomId,
      subject: this.placementData.subject,
      nonSession: isNonSession,
      isSetupSession: isSetupSession,
      tutorId: this.placementData.tutorId,
      clientId: this.clientId,
    };

    return request;
  }

  public launchVideoLibrary(): void {

    const request: LaunchVideoLibrary = {
      digitalClassroomId:
        this.placementMaintenance.placementInfo.digitalClassroomId,
      isSuperUser: this.isSuperUser,
      tutorId: this.placementData.tutorId,
      clientId: this.clientId,
      isStudent: false
    };

    this.placementService.launchVideoLibrary(request).subscribe(
      (access: DigitalClassroomAccess) => {
        setTimeout(() => {
          // See the classic flow: the access token says who may open the library, not whose
          // recordings to show, and without this parameter no search runs at all (bug 37485).
          // The tutor rather than the learner - the learner-scoped view already exists on the
          // student pages, and this is the only route to a tutor's own recordings.
          window.open(
            `${access.accessToken}&userref=${this.placementData.tutorId}TUT`,
            "_blank"
          );
        }, 0);
      },
      (error) => {
        let errorMessage =
          error.status === 401 ? "Unauthorized" : error.message;
        if (
          errorMessage ==
          "Object reference not set to an instance of an object."
        ) {
          errorMessage = "Client not found. Try another.";
        }
        this.errorModalHelper.show(errorMessage, error);
      }
    );
  }

  public launchOnlineOverview() {
    this.placementService
      .launchOnlineOverview({
        userId: this.userId,
        isSuperUser: this.hasVideoLibraryAllRoomsPermission,
        clientId: this.clientId,
        clientName: this.placementMaintenance.placementInfo.clientName,
      })
      .subscribe(
        (access: DigitalClassroomAccess) => {
          setTimeout(() => {
            window.open(access.accessToken, "_blank");
          }, 0);
        },
        (error) => {
          let errorMessage =
            error.status === 401 ? "Unauthorized" : error.message;
          if (
            errorMessage ==
            "Object reference not set to an instance of an object."
          ) {
            errorMessage = "Client not found. Try another.";
          }
          this.errorModalHelper.show(errorMessage, error);
        }
      );
  }

  public sendStudentFeedbackLink(student: PlacementStudentV2): void {
    let data = new SendStudentFeedbackLinkModalData();
    data.studentId = student.studentId;
    data.showEmailButton = student.studentHasEmail;
    data.placementId = this.placementData.placementID;
    data.placementStartDate = this.startDate;
    data.placementEndDate = this.endDate;

    const dialogRef = this.dialog.open(SendStudentFeedbackLinkModalComponent, {
      data: data,
      panelClass: "my-dialog",
    });
  }

  public enterStudentFeedbackManually(student: PlacementStudentV2): void {
    const dialogRef = this.dialog.open(FeedbackModalComponent, {
      data: {
        studentId: student.studentId,
        placementId: this.placementData.placementID,
        placementStartDate: this.startDate,
        placementEndDate: this.endDate,
        modalHeader: "Learner Feedback",
        isManualEntry: true,
        showHeaderText: false,
      },
      panelClass: "my-dialog",
    });
  }

  public viewSession(): void {
    this.router.navigate(["/view-sessions", this.placementData.placementID], {
      state: {
        placementId: this.placementData.placementID,
        lessonType: this.lessonPlanType
      },
      queryParams: { returnUrl: window.location.pathname },
    });
  }

  public addGroupLearningSection(): void {
    const dialogRef = this.dialog.open(EditLearningSectionModalComponent, {
      data: {
        editMode: true,
        timelineSection: null,
        add: true,
        students: this.students.map(
          (s) =>
            new TimelineSectionStudentInfo(
              s.studentId,
              s.studentFirstName + " " + s.studentLastName
            )
        ),
        subject: this.placementData.subject,
        placementId: this.placementData.placementID,
      },
      panelClass: "my-timeline-edit-dialog",
    });
    dialogRef.afterClosed().subscribe((reload: boolean) => {
      if (reload) {
        this.onDataChanged.emit(true);
      }
    });
  }

  /**
   * Offer "Set up online classroom" on a booking Orbit has as in person, so a session the tutor delivers online can be
   * run and the learner invited (39883). Only while the switch is on, for someone who can launch the classroom, and only
   * until the booking has one.
   */
  private updateOfferOnlineClassroom(): void {
    const info = this.placementMaintenance?.placementInfo;
    this.showOfferOnlineClassroom =
      this.deliverySettingOn &&
      this.hasLaunchDigitalClassroomPermission &&
      !!info &&
      (info.deliveryMode || "").toLowerCase() !== "online" &&
      !info.digitalClassroomEnabled;
  }

  /**
   * Sets the classroom up and switches the session it will launch for next to Online (39883): the API clears an In
   * person delivery setting on that session, so the tutor chooses one when finalising.
   */
  public offerOnlineClassroom(): void {
    if (this.offeringOnlineClassroom || !this.placementMaintenance?.placementInfo) return;
    this.offeringOnlineClassroom = true;
    const placementId = Number(this.tutorReportsHelperService.commonData.placementId);
    // The booking's sessions are only loaded once it has a classroom, so read them here to find the one the
    // classroom will launch for next, by the same rule the launch tile uses.
    this.sessionService.getSessionBlocks(placementId, null, null).pipe(
      switchMap((sessions: Array<any>) => {
        const next = this.pickNextSession(sessions);
        return this.placementService.offerOnlineClassroom(placementId, next?.sessionId ?? null);
      })
    ).subscribe({
      next: () => {
        this.offeringOnlineClassroom = false;
        // Reload the booking so the Online Tuition section (launch, prepare, invite) appears.
        this.populatePage();
      },
      error: (error) => {
        this.offeringOnlineClassroom = false;
        this.errorModalHelper.show(error?.message || "The online classroom could not be set up. Please try again.", error);
      },
    });
  }

  private async populatePage() {

    if (
      this.tutorReportsHelperService.commonData == null ||
      this.tutorReportsHelperService.commonData.placementId == null
    ) {
      this.router.navigate(["/"]);
    } else {
      try {
        this.placementMaintenance = await this.placementService
          .getPlacementMaintenanceInfo(
            this.tutorReportsHelperService.commonData.placementId.toString()
          )
          .toPromise();
      } catch (error) {
        let errorMessage =
          error.status === 401 ? "Unauthorized" : error.message;
        if (
          errorMessage ==
          "Object reference not set to an instance of an object."
        ) {
          errorMessage = "Client not found. Try another.";
        }
        this.errorModalHelper.show(errorMessage, error);
      }
      this.tutorReportsHelperService.setPlacementMaintenance(this.placementMaintenance);
      this.clientId = this.placementMaintenance.placementInfo.clientId;

      this.tutorReportsHelperService.commonData.applicationUserId =
        this.authService.userSessionData.currentUser.applicationUserId;

      this.showLaunchDigitalClassroom =
        this.placementMaintenance.placementInfo.digitalClassroomEnabled &&
        this.hasLaunchVideoLibraryPermission;

      this.showMissingRoomWarning =
        (this.placementMaintenance.placementInfo.deliveryMode || "").toLowerCase() === "online" &&
        (!this.placementMaintenance.placementInfo.digitalClassroomEnabled ||
          this.placementMaintenance.placementInfo.digitalClassroomId == null);
      this.updateOfferOnlineClassroom();

      // Start the launch-window clock once the booking is loaded, so the tile can show whether the
      // classroom is launchable rather than refusing the click after the fact.
      this.startLaunchWindowTracking();

      if (
        this.placementMaintenance.placementInfo.digitalClassroomEnabled &&
        this.hasLaunchDigitalClassroomPermission
      ) {
        try {
          this.sessions = await this.sessionService
            .getSessionBlocks(
              this.placementData.placementID,
              null,
              null
            )
            .toPromise();
        } catch (error) {
          let errorMessage =
            error.status === 401 ? "Unauthorized" : error.message;
          if (
            errorMessage ==
            "Object reference not set to an instance of an object."
          ) {
            errorMessage = "Client not found. Try another.";
          }
          this.errorModalHelper.show(errorMessage, error);
        }

        // Bug 37074, second half. This pre-filter dropped every session whose end had passed —
        // BEFORE findNextSession could apply the 90-minute grace — so fixing the selection alone
        // achieved nothing: the just-ended session was already gone from the array. Keep anything
        // still inside its launch window and let findNextSession make the actual choice, so the
        // rule lives in one place rather than two that can disagree.
        //
        // serverNow(), not new Date(): every other launch-window decision is made against the
        // server clock, and a browser running fast would otherwise discard a session the server
        // still considers launchable.
        const currentDateTime = this.serverNow();

        this.sessions = this.sessions.filter((session) => {
          const launchWindowCloses = this.sessionEnd(session);
          launchWindowCloses.setMinutes(
            launchWindowCloses.getMinutes() + this.launchWindowMinutes
          );
          return launchWindowCloses >= currentDateTime;
        });

        this.findNextSession();
      }

      this.students = this.placementData.studentGroup;
      if (!this.placementData.isGroup) {
        if (
          this.placementData.studentGroup != null &&
          this.placementData.studentGroup.length > 0
        ) {
          this.placementData.studentName =
            this.placementData.studentGroup[0].studentFirstName +
            " " +
            this.placementData.studentGroup[0].studentLastName;
          this.placementData.studentId =
            this.placementData.studentGroup[0].studentId;
        }
      }

      if (
        this.placementData.placementType != null &&
        (this.placementData.placementType.toLowerCase().indexOf("online") >
          -1 ||
          this.placementData.placementType.toLowerCase() == "hybrid") &&
        this.placementMaintenance.placementInfo.brambleEnabled
      ) {
        this.showLaunchOnlineRoomButton = true;
      } else {
        this.showLaunchOnlineRoomButton = false;
      }

      this.startDate = this.placementData.placementStartDate;
      this.endDate = this.placementData.placementEndDate;
      try {
        let baselineFrame = await this.placementService
          .GetBaselineFrame(
            this.tutorReportsHelperService.commonData.placementId,
            this.placementData.studentId
          )
          .toPromise();
        this.tutorReportsHelperService.setBaselineFrame(baselineFrame);
      } catch (error) {
        let errorMessage =
          error.status === 401 ? "Unauthorized" : error.message;
        if (
          errorMessage ==
          "Object reference not set to an instance of an object."
        ) {
          errorMessage = "Client not found. Try another.";
        }
        this.errorModalHelper.show(errorMessage, error);
      }
    }

  }

  /** End of a session as a real date/time, from its startDate + endTime. */
  private sessionEnd(session: any): Date {
    const end = new Date(session.startDate);
    const [h, m, s] = session.endTime.split(":");
    end.setHours(parseInt(h, 10), parseInt(m, 10), parseInt(s ?? "0", 10) || 0);
    return end;
  }

  /** Start of a session as a real date/time, from its startDate + startTime. */
  private sessionStart(session: any): Date {
    const start = new Date(session.startDate);
    const [h, m, s] = session.startTime.split(":");
    start.setHours(parseInt(h, 10), parseInt(m, 10), parseInt(s ?? "0", 10) || 0);
    return start;
  }

  private findNextSession() {
    const nextUpcomingSession = this.pickNextSession(this.sessions);

    if (nextUpcomingSession === undefined) {
      this.hasSession = false;
    } else {
      this.hasSession = true;
      this.session = nextUpcomingSession;
    }

    this.updateLaunchWindow();
  }

  /**
   * The session the classroom launches for: the chronologically nearest one whose launch window has not closed.
   * Shared by the launch tile (findNextSession) and "Set up online classroom", which switches this same session to
   * Online (39883), so the two can never disagree about which session is next.
   */
  private pickNextSession(sessions: Array<any>): any {
    const now = this.serverNow();

    // Bug 36770, same fault the classic flow already fixed: Array.find returns the first element
    // that is upcoming in ARRAY order, not the chronologically nearest, and getSessionBlocks does
    // not guarantee date order. A later session could be picked ahead of today's, so the launch
    // window was built around the wrong date and blocked access to a session well within range.
    //
    // Bug 37074: a session stays launchable for launchWindowMinutes AFTER it ends — that is what
    // updateLaunchWindow's `closes` is for, and the same grace governs launchDigitalClassroom
    // below. Filtering on the end alone dropped a session the instant it finished and promoted the
    // next one, so that grace could never be reached: the tutor was shown the next session instead
    // of the launch section they still had an hour of. Keep a session in the running until its
    // launch window has actually closed.
    const stillWithinLaunchWindow = (session: any): boolean =>
      this.sessionEnd(session).getTime()
        + this.launchWindowMinutes * 60000 > now.getTime();

    return (sessions ?? [])
      .filter(stillWithinLaunchWindow)
      .sort((a, b) => this.sessionEnd(a).getTime() - this.sessionEnd(b).getTime())[0];
  }

  private async createMultipleSessions() {
    const todayDate = new Date();

    const sessionDate = new Date(this.session.startDate);

    const endCompareTime = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      sessionDate.getDate(),
      parseInt(this.session.endTime.split(":")[0], 10),
      parseInt(this.session.endTime.split(":")[1], 10)
    );

    const isAfterSession = todayDate.getTime() > endCompareTime.getTime();

    const todaySessions = this.sessions.filter((session) => {
      let sessionDate = new Date(session.startDate);
      return sessionDate.toDateString() === todayDate.toDateString();
    });

    if (todaySessions.length > 1) {
      for (const session of todaySessions) {
        var classroomRequest = this.createLaunchDigitalClassroomModel(
          session,
          false,
          true
        );
        try {
          await this.placementService
            .launchDigitalClassroom(classroomRequest)
            .toPromise();
        } catch (error) {
          let errorMessage =
            error.status === 401 ? "Unauthorized" : error.message;
          this.errorModalHelper.show(errorMessage, error);
        }
      }
    }

    if (isAfterSession) {
      this.findNextSession();
    }
  }

  private isCurrentTimeBetween(start: Date, end: Date, current: Date): boolean {
    return current >= start && current <= end;
  }
}
